import { useMemo, useState } from "react";

import { Button } from "@/shared/components/ui/button";
import {
  type ColumnMapping,
  parseHeaders,
  requiredFieldsMapped,
  rewriteCsvWithMapping,
  suggestMapping,
} from "@/shared/import/column-mapping";
import { ColumnMappingRow, UNMAPPED } from "@/shared/import/column-mapping-row";
import {
  type ImportAccess,
  NO_GATED_ACCESS,
  resolveImportGroup,
  SECTION_LABELS,
  sectionImportFields,
} from "@/shared/import/import-group-access";
import { type ImportGroupKey, importGroup } from "@/shared/import/import-groups";
import { cn } from "@/shared/lib/cn";

interface Props {
  /// Which field catalogue the file is mapped onto.
  groupKey: ImportGroupKey;
  /// Raw CSV text the user uploaded.
  rawCsv: string;
  /// Called with the rewritten-canonical CSV text once the user confirms, and
  /// the mapping behind it so a caller can bring the user back to it.
  onConfirm: (transformedCsv: string, mapping: ColumnMapping) => void;
  onBack: () => void;
  pending?: boolean;
  /// Which permission- and plan-gated fields to offer. Omitted means none are.
  access?: ImportAccess;
  /// A mapping the user already confirmed for this same file, restored in
  /// place of a fresh suggestion when they come back to this screen.
  initialMapping?: ColumnMapping;
}

/// Two-column mapping editor. Auto-suggests matches via `suggestMapping`,
/// lets the user override each row with a dropdown of file columns. Submit is
/// disabled until every field the group marks required has a column. The
/// mapping is a transient picker UI, not a persisted form, so we keep it on
/// `useState` rather than RHF / FormDialog.
export function ColumnMappingForm({
  groupKey,
  rawCsv,
  onConfirm,
  onBack,
  pending,
  access = NO_GATED_ACCESS,
  initialMapping,
}: Props) {
  const group = resolveImportGroup(importGroup(groupKey), access);
  const fileHeaders = useMemo(() => parseHeaders(rawCsv), [rawCsv]);
  const [mapping, setMapping] = useState<ColumnMapping>(
    () => initialMapping ?? suggestMapping(group, fileHeaders),
  );
  const sections = sectionImportFields(group.fields);

  function onPick(field: string, fileColumn: string) {
    setMapping((prev) => {
      const next = { ...prev };
      // Each file column is used at most once, clear any prior owner.
      if (fileColumn !== UNMAPPED) {
        for (const key of Object.keys(next)) {
          if (next[key] === fileColumn) next[key] = null;
        }
      }
      next[field] = fileColumn === UNMAPPED ? null : fileColumn;
      return next;
    });
  }

  const ready = requiredFieldsMapped(group, mapping);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h3 className="text-sm font-semibold">Map your columns</h3>
        <p className="text-xs text-text-secondary">{group.hint}</p>
      </div>

      <div className="flex items-center gap-2 px-1 text-2xs uppercase tracking-wider text-text-tertiary">
        <span className="flex-1">Pulsewise field</span>
        <span className="w-4" />
        <span className="flex-1">Your file column</span>
        <span className="w-5" />
      </div>

      <div className="scrollbar-thin flex min-h-0 flex-1 flex-col gap-4 overflow-auto pr-1">
        {sections.map((section) => (
          <section key={section.key ?? "fields"} className="flex flex-col gap-2">
            {section.key ? (
              <h4 className="px-1 text-xs font-semibold text-text-secondary">
                {SECTION_LABELS[section.key]}
              </h4>
            ) : null}
            <ul className="flex flex-col gap-2">
              {section.fields.map((field) => (
                <ColumnMappingRow
                  key={field.name}
                  field={field}
                  picked={mapping[field.name] ?? null}
                  fileHeaders={fileHeaders}
                  onPick={onPick}
                />
              ))}
            </ul>
          </section>
        ))}
      </div>

      <div
        className={cn(
          "rounded-md border px-3 py-2 text-xs",
          ready
            ? "border-border bg-surface-subtle text-text-secondary"
            : "border-destructive/40 bg-destructive/10 text-destructive",
        )}
      >
        {ready ? group.readyMessage : group.missingMessage}
      </div>

      <div className="flex items-center justify-between gap-2">
        <Button type="button" variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button
          type="button"
          disabled={!ready || pending}
          onClick={() => onConfirm(rewriteCsvWithMapping(rawCsv, mapping, group), mapping)}
        >
          {pending ? (group.pendingLabel ?? "Importing...") : group.confirmLabel}
        </Button>
      </div>
    </div>
  );
}
